import { ethers } from "ethers";
import detectEthereumProvider from "@metamask/detect-provider";
import rAskoFarmABI from "../abis/rAskoFarm";
import rAskoSwapRouterABI from "../abis/rAskoSwapRouter";
import rAskoSwapLibraryABI from "../abis/rAskoSwapLibrary";
import rAskoSwapPairABI from "../abis/rAskoSwapPair";
import ERC20Abi from "../abis/ERC20";
import ARTABI from "../abis/AskoRiskToken";
import abis from "../abis/abis.js";
import {
  rAskoFarmAddress,
  rAskoSwapRouterAddress,
  rAskoSwapLibraryAddress,
  moneyMarketControl,
  uniswapOracleFactory,
  rAskoDistributionAddress,
  rAskoCohortAddress,
} from "../constants/addresses";
import rAskoDistributionABI from "../abis/rAskoDistribution.json";

/*
import ERC20ABI from "../abis/ERC20";
import PresalecontractABI from "../abis/PresaleContract";
import DogeforthABI from "../abis/Dogeforth";
import {
  BUSDAddress,
  PresaleAddress,
  DogeforthAddress
} from "../constants/addresses";
*/

export const getWeb3Provider = (provider) => {
  if (provider) {
    return new ethers.providers.Web3Provider(provider);
  } else {
    return null;
  }
};

export const getMetamaskProvider = async () => {
  const provider = await detectEthereumProvider();
  if (provider) {
    return provider;
  } else {
    return null;
  }
};

export const isMetamaskConnected = async (provider) => {
  return provider.isConnected();
};

export const getAccountSigner = async (web3Provider) => {
  return await web3Provider.getSigner();
};

export const connectToMetamask = async (
  metamaskProvider,
  isMetamaskConnected
) => {
  if (window.ethereum != null) {
    if (!isMetamaskConnected) {
      await window.ethereum.request({ method: "eth_requestAccounts" });
    }

    if (window.ethereum.chainId !== 56) {
      await metamaskProvider.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: "0x38",
            chainName: "Smart Chain",
            nativeCurrency: {
              name: "BNB",
              symbol: "BNB", // 2-6 characters long
              decimals: 18,
            },
            rpcUrls: ["https://bsc-dataseed.binance.org/"],
            blockExplorerUrls: ["https://bscscan.com"],
          },
        ],
      });
    }
  } else {
    console.log("Please download metamask");
  }
};

export const addMetamaskListeners = (
  provider,
  chainChangedCallback,
  messageCallback,
  accountsChangedCallback
) => {
  provider.on("chainChanged", (chainId) => {
    chainChangedCallback(chainId);
  });
  provider.on("message", (message) => {
    messageCallback(message);
  });
  provider.on("accountsChanged", (accounts) => {
    accountsChangedCallback(accounts);
  });
};

export const weiToEth = (weiBalance) => {
  return ethers.utils.formatEther(weiBalance);
};

export const ethToWei = (ethBalance) => {
  return ethers.utils.parseEther(ethBalance);
};

export const getERC20Contract = (web3Provider, address) => {
  return new ethers.Contract(address, ERC20Abi, web3Provider.getSigner());
};

export const getERC20Balance = async (tokenContract, accountAddress) => {
  return await tokenContract.balanceOf(accountAddress);
};

export const getERC20BalanceFromAddress = async (
  web3Provider,
  tokenAddress,
  accountAddress
) => {
  const contract = new ethers.Contract(
    tokenAddress,
    ERC20Abi,
    web3Provider.getSigner()
  );

  return await contract.balanceOf(accountAddress);
};

export const getERC20BalanceByAddress = async (
  web3Provider,
  tokenAddress,
  accountAddress
) => {
  const tokenContract = new ethers.Contract(
    tokenAddress,
    ERC20Abi,
    await web3Provider.getSigner()
  );

  return await tokenContract.balanceOf(accountAddress);
};

export const getETHBalance = async (address, web3) => {
  return await web3.getBalance(address);
};

export const getAssetContract = async (web3Provider, assetAddress) => {
  const assetContract = new ethers.Contract(
    assetAddress,
    ERC20Abi,
    await web3Provider.getSigner()
  );

  return assetContract;
};

export const getFarmContract = (web3Provider) => {
  return new ethers.Contract(
    rAskoFarmAddress,
    rAskoFarmABI,
    web3Provider.getSigner()
  );
};

export const getRouterContract = (web3Provider) => {
  return new ethers.Contract(
    rAskoSwapRouterAddress,
    rAskoSwapRouterABI,
    web3Provider.getSigner()
  );
};

export const getLibraryContract = (web3Provider) => {
  return new ethers.Contract(
    rAskoSwapLibraryAddress,
    rAskoSwapLibraryABI,
    web3Provider.getSigner()
  );
};

export const getMoneyMarketControlContract = async (web3Provider) => {
  const MoneyMarketControlContract = new ethers.Contract(
    moneyMarketControl,
    abis.mmcabi,
    await web3Provider.getSigner()
  );
  return MoneyMarketControlContract;
};

export const getUniswapOracleFactoryContract = async (web3Provider) => {
  const UniswapOracleFactoryContract = new ethers.Contract(
    uniswapOracleFactory,
    abis.uniswapOracleFactoryABI,
    await web3Provider.getSigner()
  );
  return UniswapOracleFactoryContract;
};

export const getAssets = async (moneyMarketControlContract) => {
  return await moneyMarketControlContract.getAssets();
};

export const getMoneyMarketInstanceContract = async (
  mmc,
  assetAddress,
  web3Provider
) => {
  const mmiAddress = await mmc.instanceTracker(assetAddress);
  const MoneyMarketInstanceContract = new ethers.Contract(
    mmiAddress,
    abis.mmiabi,
    await web3Provider.getSigner()
  );
  return MoneyMarketInstanceContract;
};

export const getARTContract = (artAddress, web3Provider) => {
  const ARTContract = new ethers.Contract(
    artAddress,
    abis.artabi,
    web3Provider.getSigner()
  );
  return ARTContract;
};

export const addLiquidity = async (
  contract,
  tokenA,
  tokenB,
  amountADesired,
  amountBDesired,
  amountAMin,
  amountBMin,
  to,
  deadline
) => {
  console.log(amountADesired, amountBDesired);
  await (
    await contract.addLiquidity(
      tokenA,
      tokenB,
      amountADesired,
      amountBDesired,
      amountAMin,
      amountBMin,
      to,
      deadline
    )
  ).wait();
};

export const removeLiquidity = async (
  contract,
  tokenA,
  tokenB,
  liquidity,
  amountAMin,
  amountBMin,
  to,
  deadline
) => {
  await (
    await contract.removeLiquidity(
      tokenA,
      tokenB,
      liquidity,
      amountAMin,
      amountBMin,
      to,
      deadline
    )
  ).wait();
};

export const calcTokenDesired = async (
  contract,
  amountADesired,
  tokenA,
  tokenB,
  libraryContract
) => {
  const factory = await contract.factory();

  let [reserveA, reserveB] = await libraryContract.getReserves(
    factory,
    tokenA,
    tokenB
  );

  let amountBOptimal = await libraryContract.quote(
    amountADesired,
    reserveA,
    reserveB
  );
  return amountBOptimal;
};

export const calcPrice = async (contract, tokenA, tokenB, libraryContract) => {
  const factory = await contract.factory();

  let [reserveA, reserveB] = await libraryContract.getReserves(
    factory,
    tokenA,
    tokenB
  );

  if (
    tokenA == "0xaa50040d1b969086883a79b324b9cd4565642d99" ||
    tokenA == "0x31bb1f238105ae81ab63208849596cf49eb54b95"
  ) {
    reserveA = ethers.utils.formatUnits(reserveA.toString(), 6);
  } else {
    reserveA = ethers.utils.formatEther(reserveA.toString());
  }

  if (
    tokenB == "0xaa50040d1b969086883a79b324b9cd4565642d99" ||
    tokenB == "0x31bb1f238105ae81ab63208849596cf49eb54b95"
  ) {
    reserveB = ethers.utils.formatUnits(reserveB.toString(), 6);
  } else {
    reserveB = ethers.utils.formatEther(reserveB.toString());
  }

  const price = reserveB / reserveA;

  return price;
};

export const calcTokensOut = async (
  contract,
  amountIn,
  path,
  libraryContract
) => {
  const factory = await contract.factory();

  const amounts = await libraryContract.getAmountsOut(
    factory,
    amountIn,
    path,
    0
  );

  return amounts[1];
};

export const calcFee = async (
  contract,
  amountIn,
  amountOutMin,
  path,
  to,
  deadline,
  libraryContract
) => {
  const DAOFee = await contract.DAOFee();
  const OperatingFee = await contract.OperatingFee();
  const BuybackFee = await contract.BuybackFee();
  const LPFee = await contract.LPFee();

  const factory = await contract.factory();

  const baseLR = await contract.baseLR();
  const baseHR = await contract.baseHR();

  //console.log(factory, amountIn, path);

  const amounts = await libraryContract.getAmountsOut(
    factory,
    amountIn,
    path,
    0
  );

  let totalFee;

  if (
    path[0].toLowerCase() === baseLR.toLowerCase() ||
    path[0].toLowerCase() === baseHR.toLowerCase()
  ) {
    const DAORemainder = (amountIn * DAOFee) / 10000;
    const OperatingRemainder = (amountIn * OperatingFee) / 10000;
    const BuybackRemainder = (amountIn * BuybackFee) / 10000;
    const LpRemainder = (amountIn * LPFee) / 10000;
    totalFee =
      DAORemainder + OperatingRemainder + BuybackRemainder + LpRemainder;
  } else {
    const DAORemainder = (amounts[1] * DAOFee) / 10000;
    const OperatingRemainder = (amounts[1] * OperatingFee) / 10000;
    const BuybackRemainder = (amounts[1] * BuybackFee) / 10000;
    const LpRemainder = (amounts[1] * LPFee) / 10000;
    totalFee =
      DAORemainder + OperatingRemainder + BuybackRemainder + LpRemainder + "";
  }

  return totalFee;
};

export const swap = async (
  contract,
  amountIn,
  amountOutMin,
  path,
  to,
  deadline,
  libraryContract
) => {
  const DAOFee = await contract.DAOFee();
  const OperatingFee = await contract.OperatingFee();
  const BuybackFee = await contract.BuybackFee();
  const LPFee = await contract.LPFee();

  const factory = await contract.factory();

  const baseLR = await contract.baseLR();
  const baseHR = await contract.baseHR();

  const amounts = await libraryContract.getAmountsOut(
    factory,
    amountIn,
    path,
    0
  );

  let totalFee;

  if (
    path[0].toLowerCase() === baseLR.toLowerCase() ||
    path[0].toLowerCase() === baseHR.toLowerCase()
  ) {
    const DAORemainder = (amountIn * DAOFee) / 10000;
    const OperatingRemainder = (amountIn * OperatingFee) / 10000;
    const BuybackRemainder = (amountIn * BuybackFee) / 10000;
    const LpRemainder = (amountIn * LPFee) / 10000;
    totalFee =
      DAORemainder + OperatingRemainder + BuybackRemainder + LpRemainder;

    console.log(
      DAORemainder,
      OperatingRemainder,
      BuybackRemainder,
      LpRemainder
    );
  } else {
    const DAORemainder = (amounts[1] * DAOFee) / 10000;
    const OperatingRemainder = (amounts[1] * OperatingFee) / 10000;
    const BuybackRemainder = (amounts[1] * BuybackFee) / 10000;
    const LpRemainder = (amounts[1] * LPFee) / 10000;
    totalFee =
      DAORemainder + OperatingRemainder + BuybackRemainder + LpRemainder + "";
  }

  // console.log(parseInt(totalFee).toString())

  console.log(amountOutMin.toString());

  await (
    await contract.swapExactTokensForTokens(
      amountIn,
      amountOutMin,
      path,
      to,
      parseInt(deadline),
      {
        value: parseInt(totalFee).toString(),
      }
    )
  ).wait();
};

export const deposit = async (contract, pid, amount) => {
  return await (await contract.deposit(pid, ethToWei(amount))).wait();
};

export const withdraw = async (contract, pid, amount) => {
  return await (await contract.withdraw(pid, ethToWei(amount))).wait();
};

export const claimRewards = async (contract, pid) => {
  return await (await contract.withdraw(pid, 0)).wait();
};

export const approveErc20 = async (web3Provider, address, spender) => {
  const contract = new ethers.Contract(
    address,
    ERC20Abi,
    web3Provider.getSigner()
  );

  return await (
    await contract.approve(
      spender,
      "115792089237316195423570985008687907853269984665640564039457584007913129639935"
    )
  ).wait();
};

export const isErc20Approved = async (
  web3Provider,
  address,
  ownerAddress,
  spender
) => {
  const contract = new ethers.Contract(
    address,
    ERC20Abi,
    web3Provider.getSigner()
  );

  const allowance = await contract.allowance(ownerAddress, spender);

  return allowance.gt(ethers.utils.parseEther("10000"));
};

export const isApprovedForAmount = async (
  web3Provider,
  address,
  ownerAddress,
  spender,
  amount
) => {
  const contract = new ethers.Contract(
    address,
    ERC20Abi,
    web3Provider.getSigner()
  );

  const allowance = await contract.allowance(ownerAddress, spender);

  return allowance.gt(ethers.utils.parseEther(amount));
};

export const getPairContract = (web3Provider, address) => {
  return new ethers.Contract(
    address,
    rAskoSwapPairABI,
    web3Provider.getSigner()
  );
};

export const getDistributionContract = async (web3Provider) => {
  return new ethers.Contract(
    rAskoDistributionAddress,
    rAskoDistributionABI,
    await web3Provider.getSigner()
  );
};

export const getCohortContract = async (web3Provider) => {
  return new ethers.Contract(
    rAskoCohortAddress,
    abis.cohortabi,
    await web3Provider.getSigner()
  );
};
